import useProductQueries from '@dashboard/hooks/queries/useProductQueries';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import clsx from 'clsx';
import { FieldArray, Formik } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import React, { useState } from 'react';
import * as Yup from 'yup';

import Features from '@dashboard/enums/features';
import { confirm } from '@dashboard/lib/confirm';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceInput } from '@dashboard/services/formatting';
import getCapitalizedSubscriptionInterval from '@dashboard/services/formatting/getCapitalizedSubscriptionInterval';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import getProductType, { PRODUCT_TYPES } from '@dashboard/services/getProductType';
import logException from '@dashboard/services/logException';
import { getPriceBillingConfig } from '@repo/checkout';

import Button from '@dashboard/components/Button';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Collapsible from '@dashboard/components/Collapsible';
import { FormElement, Label } from '@dashboard/components/Form';
import {
  Checkbox,
  FormSubmit,
  Listbox,
  Radio,
  Select,
  TextInput,
  Textarea,
} from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { ImageField } from '@dashboard/components/Formik/components/Image';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import Reorderable from '@dashboard/components/Reorderable';
import Tooltip, { InfoTooltip } from '@dashboard/components/Tooltip';

import useProduct from '../../../useProduct';
import { useVariantHelpers } from '../useVariantHelpers';
import { getInitialVariantOption } from '../useVariantOption';
import { useVariantEdit } from '../VariantEditContext';
import { useVariants } from '../VariantsContext';
import ShowHideLogicField from './ShowHideLogicField';
import { getPriceLabel } from '../../PricesList/priceHelpers';
import { UnsavedChangesReporter } from '@dashboard/components/UnsavedChanges';

const getValidationSchema = product => {
  return Yup.object().shape({
    options: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Required'),
        description: Yup.string(),
        type: Yup.string()
          .required('Billing is required')
          .oneOf(['one_time', 'recurring', 'pay_what_you_want']),
        // TODO(M14): edits legacy product scalars for pre-migration products — remove with the legacy fields after the backfill migration (product.currency/type)
        additionalChargeAmount: Yup.string()
          .test('formats-to-number', 'Should be a number', function (value) {
            if (this.parent.type === 'pay_what_you_want') {
              if (!value && value !== '0' && value !== 0) return true;
            }

            const formattedValue = formatPriceInput(value, product.currency);

            if (typeof formattedValue === 'number') {
              return true;
            }

            return false;
          })
          .nullable()
          .test('is-positive', 'Must be 0 or more', function (value) {
            const formattedValue = formatPriceInput(value, product.currency);

            const productType = getProductType(product);
            if (
              productType === PRODUCT_TYPES.SUBSCRIPTION ||
              productType === PRODUCT_TYPES.PAYMENT_PLAN ||
              this.parent.type === 'pay_what_you_want'
            ) {
              return !formattedValue || formattedValue >= 0;
            }

            return true;
          }),
      }),
    ),
    // On a multi-price product the merchant must scope the variant to at least
    // one price. Single-price products auto-scope on submit (no field shown).
    priceIds:
      (product.prices?.length ?? 0) > 1
        ? Yup.array().of(Yup.string()).min(1, 'Select at least one price for this variant.')
        : Yup.array().of(Yup.string()).nullable(),
  });
};

export const getInitialValues = (variant, currency, productType, productPrices = []) => ({
  name: variant.name,
  required: variant.required,
  // Drop ids of prices that no longer exist on the product. A variant scoped
  // to a since-deleted price (data from before deletion was blocked) is
  // otherwise unrecoverable: the dead id isn't among the multiselect's
  // options so it can't be deselected, and every save 400s in
  // validateVariantScope. Filtering lets the merchant re-scope and save.
  priceIds: (() => {
    const ids = (variant.priceIds || []).map(String);
    if (!productPrices.length) return ids;
    const known = new Set(productPrices.map(p => String(p._id || p.key)));
    return ids.filter(id => known.has(id));
  })(),
  options: variant.options.map(option => ({
    ...option,
    type: option.payWhatYouWant
      ? 'pay_what_you_want'
      : option.type ||
        (productType === PRODUCT_TYPES.SUBSCRIPTION || productType === PRODUCT_TYPES.PAYMENT_PLAN
          ? 'recurring'
          : 'one_time'),
    additionalChargeAmount:
      option.additionalChargeAmount &&
      formatPriceForInput({
        amount: option.additionalChargeAmount,
        currency: currency,
        style: 'decimal',
      }),
    discountedFromPrice:
      option.discountedFromPrice &&
      formatPriceForInput({
        amount: option.discountedFromPrice,
        currency: currency,
        style: 'decimal',
      }),
    pwywSuggestedPrice:
      option.pwywSuggestedPrice &&
      formatPriceForInput({
        amount: option.pwywSuggestedPrice,
        currency: currency,
        style: 'decimal',
      }),
  })),
  manageVariantOptionStock: variant.manageVariantOptionStock,
  reference: variant.reference,
  selectionType: variant.selectionType,
  increasesWithQuantity: variant.increasesWithQuantity,
  preselect: variant.preselect,
  showHideLogic: variant.showHideLogic,
  hidden: variant.hidden,
  layout: {
    variantOptionLayout: variant.layout?.variantOptionLayout || 'list',
    spacing: variant.layout?.spacing || 'comfortable',
    collapseVariantOptions:
      typeof variant.layout?.collapseVariantOptions === 'boolean'
        ? variant.layout?.collapseVariantOptions
        : false,
    variantOptionColumns:
      typeof variant.layout?.variantOptionColumns === 'number'
        ? variant.layout?.variantOptionColumns
        : 2,
    showVariantName:
      typeof variant.layout?.showVariantName === 'boolean' ? variant.layout?.showVariantName : true,
    showVariantOptionNames:
      typeof variant.layout?.showVariantOptionNames === 'boolean'
        ? variant.layout?.showVariantOptionNames
        : true,
    showVariantOptionPrices:
      typeof variant.layout?.showVariantOptionPrices === 'boolean'
        ? variant.layout?.showVariantOptionPrices
        : true,
    showVariantOptionPriceSign:
      typeof variant.layout?.showVariantOptionPriceSign === 'boolean'
        ? variant.layout?.showVariantOptionPriceSign
        : false,
    showPwywPlaceholder:
      typeof variant.layout?.showPwywPlaceholder === 'boolean'
        ? variant.layout?.showPwywPlaceholder
        : true,
    textAlign: typeof variant.layout?.textAlign === 'string' ? variant.layout?.textAlign : 'left',
    imageSize: typeof variant.layout?.imageSize === 'string' ? variant.layout?.imageSize : 'small',
  },
});

export default function VariantEdit({ variant }) {
  const { product } = useProduct();
  const { saveVariant } = useProductQueries();

  const { dispatch } = useVariants();

  const { onSaveVariant, onDirty, onCancelAddVariant } = useVariantHelpers(
    variant._id || variant.key,
  );

  const { setIsEditingVariant } = useVariantEdit();

  const [layoutOpen, setLayoutOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const variantId = variant._id || variant.key;
  const productPrices = product.prices || [];

  const validationSchema = getValidationSchema(product);
  // TODO(M14): product.currency legacy read — remove with the legacy fields after the backfill migration
  const initialValues = getInitialValues(
    variant,
    product.currency,
    getProductType(product),
    productPrices,
  );

  async function onFormSubmit(values, actions) {
    actions.setStatus(undefined);

    const body = {
      ...values,
      status: variant.status,
      // A variant must scope to at least one price. A single-price product
      // auto-scopes to its one price (no picker shown); multi-price uses the
      // merchant's selection. The API enforces the same rule as a backstop.
      priceIds:
        productPrices.length === 1
          ? [String(productPrices[0]._id || productPrices[0].key)]
          : values.priceIds || [],
      // TODO(M14): edits legacy product scalars for pre-migration products — remove with the legacy fields after the backfill migration (product.currency)
      options: values.options.map(option => ({
        ...option,
        additionalChargeAmount:
          option.additionalChargeAmount &&
          formatPriceInput(option.additionalChargeAmount, product.currency),
        discountedFromPrice:
          option.type === 'pay_what_you_want'
            ? undefined
            : option.discountedFromPrice &&
              formatPriceInput(option.discountedFromPrice, product.currency),
        pwywSuggestedPrice:
          option.type === 'pay_what_you_want' && option.pwywSuggestedPrice
            ? formatPriceInput(option.pwywSuggestedPrice, product.currency)
            : option.type !== 'pay_what_you_want'
              ? undefined
              : null,
        type: option.type === 'pay_what_you_want' ? 'one_time' : option.type,
        payWhatYouWant: option.type === 'pay_what_you_want',
      })),
    };

    delete body.isEditing;
    delete body.isNew;

    const { data, error } = await saveVariant(body, variant?._id, product._id);

    if (error) {
      logException(error);
      const { status, errors } = transformApiErrorToFormik(error);

      if (!isEmpty(errors)) {
        actions.setErrors(errors);
      } else {
        actions.setStatus(status);
      }
    } else {
      actions.setStatus('');

      actions.resetForm({
        values: values,
      });

      dispatch({
        type: 'update',
        variant,
        values: {
          ...data,
          isEditing: true,
        },
      });

      onSaveVariant();
    }
  }

  return (
    <div>
      <Formik
        key={variantId}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
      >
        {({ handleSubmit, dirty, status, values, setFieldValue }) => {
          onDirty(dirty);

          // A recurring variant option can only be scoped to all-recurring
          // prices (validateVariantScope). Derive whether the variant's scoped
          // prices (its priceIds, or all prices when unscoped) include a
          // one-time payment; if so, disable the Recurring billing option so
          // an invalid combo can't be picked — a one-time/PWYW price can't
          // charge a variant on a cycle. Legacy subscription/payment-plan
          // products (no prices[]) keep their interval-labelled recurring
          // option via the branch below.
          const scopedPrices = values.priceIds?.length
            ? productPrices.filter(p => values.priceIds.includes(String(p._id || p.key)))
            : productPrices;
          // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.type gate)
          const isLegacySubscriptionBilling =
            (product.type === PRODUCT_TYPES.SUBSCRIPTION ||
              product.type === PRODUCT_TYPES.PAYMENT_PLAN) &&
            !(product.prices?.length > 1);
          // A migrated single-price product carries billing on prices[0], not
          // the legacy scalars — resolve the legacy-labelled recurring option
          // from the price first.
          // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run
          const legacyBillingConfig = getPriceBillingConfig(productPrices[0]) || {};
          const legacyPlanIterations = legacyBillingConfig.planIterations ?? product.planIterations;
          const legacyInterval = legacyBillingConfig.interval ?? product.interval;
          const legacyIntervalCount = legacyBillingConfig.intervalCount ?? product.intervalCount;
          const recurringScopeDisabled =
            !isLegacySubscriptionBilling && scopedPrices.some(p => p.billingType === 'one_time');

          // The reverse guard: once the variant has a recurring option, a
          // one-time price can't be in its scope (validateVariantScope rejects
          // it), so disable one-time prices in the "Applies to prices" picker.
          const hasRecurringOption = (values.options || []).some(o => o.type === 'recurring');

          return (
            <form onSubmit={handleSubmit} key={variantId}>
              <UnsavedChangesReporter />
              <FormElement>
                <Label htmlFor={`variants.${variantId}.name`}>Variant name</Label>
                <TextInput
                  id={`variants.${variantId}.name`}
                  name="name"
                  type="text"
                  placeholder="Name"
                  autoComplete="off"
                  autoFocus
                />
                <FormError name="name" />
              </FormElement>

              {productPrices.length > 1 && (
                <FormElement>
                  <Label className="flex items-center">
                    Applies to prices
                    <InfoTooltip>
                      Choose which prices this variant applies to. Customers only see it when one of
                      those prices is selected.
                    </InfoTooltip>
                  </Label>
                  <Listbox
                    multiple
                    summary
                    name="priceIds"
                    placeholder="Select prices"
                    options={productPrices.map(price => ({
                      value: String(price._id || price.key),
                      name: getPriceLabel(price),
                      disabled: hasRecurringOption && price.billingType === 'one_time',
                    }))}
                  />
                  <FormError name="priceIds" />
                </FormElement>
              )}

              <Label className="pb-2">Options</Label>

              <div className={`mb-4`}>
                <FieldArray
                  name="options"
                  render={arrayHelpers => {
                    function onAddVariantOption() {
                      arrayHelpers.push(getInitialVariantOption(product));
                    }
                    return (
                      <>
                        <Reorderable.Wrapper>
                          <Reorderable
                            id={`variants.${variantId}.options`}
                            items={values?.options}
                            onReorder={options => setFieldValue('options', options)}
                            renderItem={({ index, isDragging, dragHandleProps }) => {
                              async function onDeleteVariantOption() {
                                const del = await confirm('Delete this option?', {
                                  confirm: 'Delete',
                                  ...(values.options[index].files?.length > 0 && {
                                    description:
                                      'Past customers might lose access to attached files. Disable this option to avoid this.',
                                  }),
                                });

                                if (del) {
                                  arrayHelpers.remove(index);
                                }
                              }

                              function onEnableDisableVariantOption() {
                                const status =
                                  values.options[index].status === 'enabled'
                                    ? 'disabled'
                                    : 'enabled';

                                arrayHelpers.replace(index, {
                                  ...values.options[index],
                                  status,
                                });

                                if (status === 'enabled') {
                                  setFieldValue(`options[${index}].status`, 'enabled');
                                } else {
                                  setFieldValue(`options[${index}].status`, 'disabled');
                                }
                              }

                              function onDuplicateVariantOption() {
                                const newOption = {
                                  ...values.options[index],
                                  name: `${values.options[index].name} (copy)`,
                                  key: uniqueId(),
                                };

                                delete newOption._id;
                                delete newOption.id;
                                delete newOption.stripeProductId;
                                delete newOption.stripePriceId;

                                arrayHelpers.insert(index + 1, newOption);
                              }

                              const optionDetailsGridClass = values.manageVariantOptionStock
                                ? 'grid-cols-2'
                                : 'grid-cols-1';

                              return (
                                <Reorderable.Item
                                  isLast={index === values.options.length - 1}
                                  isDragging={isDragging}
                                  border={false}
                                >
                                  <div className="flex w-full mb-5">
                                    <div className="flex-1 min-w-0 relative">
                                      {values.options[index].status === 'disabled' && (
                                        <div className="absolute inset-x-0 top-14 bottom-0 z-10 bg-white/60 dark:bg-background/60 cursor-not-allowed"></div>
                                      )}

                                      <div className="flex items-center gap-x-1">
                                        <div
                                          {...dragHandleProps}
                                          className={clsx(
                                            buttonVariants({ variant: 'ghost', size: 'icon' }),
                                            'shrink-0 cursor-grab active:cursor-grabbing',
                                          )}
                                          aria-label={`Reorder option ${index + 1}`}
                                        >
                                          <GripVertical className="size-4" />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between">
                                            <div className="min-w-0">
                                              <div className="text-sm font-medium text-gray-900 dark:text-foreground">
                                                {`Option ${index + 1}`}
                                              </div>
                                            </div>

                                            <div className="flex items-center gap-x-2 shrink-0">
                                              {values.options[index].status === 'disabled' && (
                                                <Tooltip label="Disabled options will not be shown to customers">
                                                  <Badge variant="secondary">Disabled</Badge>
                                                </Tooltip>
                                              )}

                                              <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                  <ShadcnButton
                                                    variant="ghost"
                                                    size="icon"
                                                    aria-label="More options"
                                                  >
                                                    <EllipsisVertical className="size-4" />
                                                  </ShadcnButton>
                                                </DropdownMenuTrigger>

                                                <DropdownMenuContent align="end">
                                                  <DropdownMenuItem
                                                    onSelect={onDuplicateVariantOption}
                                                  >
                                                    Duplicate
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem
                                                    onSelect={onEnableDisableVariantOption}
                                                  >
                                                    {values.options[index].status === 'enabled'
                                                      ? 'Disable'
                                                      : 'Enable'}
                                                  </DropdownMenuItem>
                                                  <DropdownMenuItem
                                                    onSelect={onDeleteVariantOption}
                                                    variant="destructive"
                                                  >
                                                    Delete
                                                  </DropdownMenuItem>
                                                </DropdownMenuContent>
                                              </DropdownMenu>
                                            </div>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="pl-9">
                                        <div className="flex gap-x-3 mb-4">
                                          <div className="w-[63px] h-[63px] shrink-0">
                                            <ImageField
                                              id={`options[${index}].image`}
                                              name={`options[${index}].image`}
                                              image={values.options[index].image}
                                              previewSize="variant_option"
                                              variant="minimal"
                                            />
                                          </div>

                                          <div className="flex-1 min-w-0">
                                            <TextInput
                                              id={`options[${index}].name`}
                                              name={`options[${index}].name`}
                                              type="text"
                                              placeholder="Name (supports HTML)"
                                              className="rounded-b-none focus:z-10 text-sm !py-[6px] relative"
                                              autoComplete="off"
                                              required
                                              border
                                              autoFocus={
                                                !!values.name && !values.options[index].name
                                              }
                                            />

                                            <Textarea
                                              id={`options[${index}].description`}
                                              name={`options[${index}].description`}
                                              className="placeholder-gray-300 dark:placeholder-muted-foreground border-t-0 rounded-t-none -mt-px focus:z-10 text-sm !py-[6px] relative"
                                              placeholder="Short description"
                                              value={values.options[index].description || undefined}
                                              autosize={true}
                                              minRows={1}
                                            />
                                          </div>
                                        </div>

                                        <div>
                                          <div className="grid grid-cols-2 gap-3 mb-4 border-b border-gray-100 dark:border-border pb-4">
                                            <div>
                                              <Label
                                                htmlFor={`options[${index}].type`}
                                                className="flex items-center"
                                              >
                                                Billing
                                                <InfoTooltip>
                                                  Choose how this option is charged: one-time, pay
                                                  what you want or recurring. Recurring is only
                                                  available for recurring prices.
                                                </InfoTooltip>
                                              </Label>

                                              <Select
                                                id={`options[${index}].type`}
                                                name={`options[${index}].type`}
                                                placeholder="Billing model"
                                                className="text-sm !py-[6px]"
                                                onChange={e => {
                                                  const nextType = e.target.value;

                                                  setFieldValue(`options[${index}].type`, nextType);
                                                  setFieldValue(
                                                    `options[${index}].discountedFromPrice`,
                                                    '',
                                                  );
                                                  setFieldValue(
                                                    `options[${index}].pwywSuggestedPrice`,
                                                    '',
                                                  );

                                                  return false;
                                                }}
                                              >
                                                <option value="" disabled>
                                                  Select billing
                                                </option>
                                                <option value="one_time">One-time</option>
                                                <option value="pay_what_you_want">
                                                  Pay what you want
                                                </option>
                                                {/* Recurring only renders where it can apply:
                                                    legacy subscriptions/payment plans get the
                                                    interval-labelled option; priced products get
                                                    it selectable unless the scope includes a
                                                    one-time payment (disabled with a hint then,
                                                    since re-scoping happens in this same form). */}
                                                {isLegacySubscriptionBilling ? (
                                                  <option value="recurring">
                                                    {legacyPlanIterations > 1 ? (
                                                      <>
                                                        {'Payment plan — '}
                                                        {getCapitalizedSubscriptionInterval(
                                                          legacyInterval,
                                                          legacyIntervalCount,
                                                        )}
                                                      </>
                                                    ) : (
                                                      getCapitalizedSubscriptionInterval(
                                                        legacyInterval,
                                                        legacyIntervalCount,
                                                      )
                                                    )}
                                                  </option>
                                                ) : productPrices.length > 0 ? (
                                                  <option
                                                    value="recurring"
                                                    disabled={recurringScopeDisabled}
                                                  >
                                                    {recurringScopeDisabled
                                                      ? 'Recurring (needs all-recurring prices)'
                                                      : 'Recurring'}
                                                  </option>
                                                ) : null}
                                              </Select>
                                            </div>

                                            <div>
                                              <Label
                                                htmlFor={`options[${index}].additionalChargeAmount`}
                                              >
                                                {values.options[index].type === 'pay_what_you_want'
                                                  ? 'Min. price'
                                                  : 'Price'}
                                              </Label>

                                              <CanUseFeature feature={Features.VARIANT_PRICE} inset>
                                                <TextInput
                                                  id={`options[${index}].additionalChargeAmount`}
                                                  name={`options[${index}].additionalChargeAmount`}
                                                  className="text-sm !py-[6px]"
                                                  type="number"
                                                  placeholder={
                                                    values.options[index].type ===
                                                    'pay_what_you_want'
                                                      ? 'Min. price'
                                                      : 'Price'
                                                  }
                                                  border
                                                />
                                              </CanUseFeature>
                                            </div>

                                            {values.options[index].type === 'pay_what_you_want' ? (
                                              <div>
                                                <Label
                                                  htmlFor={`options[${index}].pwywSuggestedPrice`}
                                                >
                                                  Suggested price
                                                </Label>

                                                <CanUseFeature
                                                  feature={Features.VARIANT_PRICE}
                                                  inset
                                                >
                                                  <TextInput
                                                    id={`options[${index}].pwywSuggestedPrice`}
                                                    name={`options[${index}].pwywSuggestedPrice`}
                                                    className="text-sm !py-[6px]"
                                                    type="number"
                                                    placeholder="Suggested price"
                                                    border
                                                  />
                                                </CanUseFeature>
                                              </div>
                                            ) : (
                                              <div>
                                                <Label
                                                  htmlFor={`options[${index}].discountedFromPrice`}
                                                >
                                                  Discounted from
                                                </Label>

                                                <CanUseFeature
                                                  feature={Features.VARIANT_PRICE}
                                                  inset
                                                >
                                                  <TextInput
                                                    id={`options[${index}].discountedFromPrice`}
                                                    name={`options[${index}].discountedFromPrice`}
                                                    className="text-sm !py-[6px]"
                                                    type="number"
                                                    placeholder="Discounted from"
                                                    border
                                                  />
                                                </CanUseFeature>
                                              </div>
                                            )}
                                          </div>

                                          <div
                                            className={clsx(
                                              'grid gap-2 mb-4',
                                              optionDetailsGridClass,
                                            )}
                                          >
                                            {values.manageVariantOptionStock && (
                                              <div>
                                                <Label htmlFor={`options[${index}].stock`}>
                                                  Stock
                                                </Label>
                                                <TextInput
                                                  id={`options[${index}].stock`}
                                                  name={`options[${index}].stock`}
                                                  className="text-sm !py-[6px]"
                                                  type="number"
                                                  placeholder="Stock"
                                                  border
                                                />
                                              </div>
                                            )}

                                            <div>
                                              <Label htmlFor={`options[${index}].sku`}>SKU</Label>
                                              <TextInput
                                                id={`options[${index}].sku`}
                                                name={`options[${index}].sku`}
                                                className="text-sm !py-[6px]"
                                                type="text"
                                                placeholder="SKU"
                                                border
                                              />
                                            </div>
                                          </div>

                                          <div>
                                            <FormElement className="!mb-0">
                                              <Label htmlFor="files">
                                                Files included with this option
                                              </Label>

                                              <Listbox
                                                id={`options[${index}].files`}
                                                name={`options[${index}].files`}
                                                options={product.files?.map(file => ({
                                                  value: file._id,
                                                  name: file.name,
                                                }))}
                                                placeholder="Select files to attach"
                                                multiple
                                                selectAll
                                                selectAllLabel="All files"
                                                emptyStateTitle="No files uploaded yet"
                                                emptyStateDescription="Upload files in Product → File downloads to attach them to this option."
                                              />
                                            </FormElement>
                                          </div>
                                        </div>
                                      </div>

                                      <FormError name={`options[${index}].name`} />
                                      <FormError
                                        name={`options[${index}].additionalChargeAmount`}
                                      />
                                      <FormError name={`options[${index}].discountedFromPrice`} />
                                      <FormError name={`options[${index}].type`} />
                                      <FormError name={`options[${index}].sku`} />
                                      <FormError name={`options[${index}].stock`} />
                                      <FormError name={`options[${index}].image`} />
                                    </div>
                                  </div>
                                </Reorderable.Item>
                              );
                            }}
                          />
                        </Reorderable.Wrapper>

                        <Button
                          handleClick={onAddVariantOption}
                          appearance="secondary"
                          type="button"
                          className="mb-2 mt-2"
                          size="small"
                        >
                          + Add option
                        </Button>
                      </>
                    );
                  }}
                />
              </div>

              <Collapsible.Root
                open={settingsOpen}
                onOpenChange={open => {
                  if (open) {
                    setSettingsOpen(true);
                    setLayoutOpen(false);
                  } else {
                    setSettingsOpen(false);
                  }
                }}
              >
                <Collapsible.Trigger className="flex items-center gap-x-1 mt-4 text-gray-700 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground font-medium text-sm">
                  <>
                    <div className="flex-1 text-left">Additional settings</div>

                    {settingsOpen ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </>
                </Collapsible.Trigger>

                <Collapsible.Content className="CollapsibleContentAnimation mt-4">
                  <FormElement className="space-y-2">
                    <Checkbox.Wrapper>
                      <Checkbox id={`variants.${variantId}.required`} name="required" />
                      <Checkbox.Label htmlFor={`variants.${variantId}.required`}>
                        Required
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="required" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.preselect.enabled`}
                        name="preselect.enabled"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.preselect.enabled`}>
                        Preselect option
                      </Checkbox.Label>
                      <FormError name="preselect.enabled" />
                    </Checkbox.Wrapper>

                    {values.preselect?.enabled && (
                      <>
                        <FormElement className="ml-[26px]">
                          <Select
                            id={`variants.${variantId}.preselect.optionId`}
                            name="preselect.optionId"
                          >
                            <option value="" default>
                              Select option
                            </option>
                            {values.options.map((option, index) => (
                              <option key={index} value={option._id || option.key}>
                                {option.name}
                              </option>
                            ))}
                          </Select>
                          <FormError name="preselect.optionId" />
                        </FormElement>

                        {values.selectionType === 'quantity' && (
                          <FormElement className="ml-[26px]">
                            <Label htmlFor={`variants.${variantId}.preselect.quantity`}>
                              Preselect quantity
                            </Label>

                            <TextInput
                              type="number"
                              id={`variants.${variantId}.preselect.quantity`}
                              name="preselect.quantity"
                              placeholder="Preselect quantity"
                            />
                            <FormError name="preselect.quantity" />
                          </FormElement>
                        )}
                      </>
                    )}

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.showHideLogic.enabled`}
                        name="showHideLogic.enabled"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.showHideLogic.enabled`}>
                        Conditional logic{' '}
                        <InfoTooltip>
                          {`Show this variant based on another variant's
                              selection.`}{' '}
                          <Link
                            to="/docs/articles/conditional-logic-for-product-variants"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="showHideLogic.enabled" />

                    {values.showHideLogic?.enabled && (
                      <ShowHideLogicField
                        variantId={variantId}
                        variant={values}
                        product={product}
                      />
                    )}

                    <Checkbox.Wrapper>
                      <Checkbox id={`variants.${variantId}.hidden`} name="hidden" />
                      <Checkbox.Label htmlFor={`variants.${variantId}.hidden`}>
                        Hide variant{' '}
                        <InfoTooltip>
                          Hide this variant and show or fill it with query parameters.{' '}
                          <Link
                            to="/docs/articles/preselect-checkout-variants"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="hidden" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.selectionType`}>Variant selection</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.single`}
                          name="selectionType"
                          value="single"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.single`}>
                          Single select
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.multiple`}
                          name="selectionType"
                          value="multiple"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.multiple`}>
                          Multiple select
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.quantity`}
                          name="selectionType"
                          value="quantity"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.quantity`}>
                          Quantity select
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>

                    <FormError name="quantity" />
                  </FormElement>

                  {(values.selectionType === 'single' || values.selectionType === 'multiple') && (
                    <FormElement>
                      <Checkbox.Wrapper>
                        <Checkbox
                          id={`variants.${variantId}.increasesWithQuantity`}
                          name="increasesWithQuantity"
                        />
                        <Checkbox.Label htmlFor={`variants.${variantId}.increasesWithQuantity`}>
                          Use checkout quantity{' '}
                          <InfoTooltip>
                            Add a quantity field and let the price of the variant option multiply
                            with the checkout quantity
                          </InfoTooltip>
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                      <FormError name="increasesWithQuantity" />
                    </FormElement>
                  )}

                  <Label>Inventory</Label>
                  <FormElement className="space-y-2">
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.manageVariantOptionStock`}
                        name="manageVariantOptionStock"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.manageVariantOptionStock`}>
                        Manage stock
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="manageVariantOptionStock" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.reference`}>
                      Reference{' '}
                      <InfoTooltip>
                        Use this reference to preselect the variant and to use the selected option
                        in Zapier.{' '}
                        <Link
                          to="/docs/articles/preselect-checkout-variants"
                          target="_blank"
                          variant="light"
                        >
                          Learn more
                        </Link>
                      </InfoTooltip>
                    </Label>

                    <TextInput
                      id={`variants.${variantId}.reference`}
                      name="reference"
                      type="text"
                      placeholder="Reference"
                    />
                    <FormError name="reference" />
                  </FormElement>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root
                open={layoutOpen}
                onOpenChange={open => {
                  if (open) {
                    setLayoutOpen(true);
                    setSettingsOpen(false);
                  } else {
                    setLayoutOpen(false);
                  }
                }}
              >
                <Collapsible.Trigger className="flex items-center gap-x-1 mt-4 text-gray-700 dark:text-muted-foreground hover:text-gray-900 dark:hover:text-foreground font-medium text-sm">
                  <div className="flex-1 text-left">Variant layout</div>
                  {layoutOpen ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </Collapsible.Trigger>

                <Collapsible.Content className="CollapsibleContentAnimation mt-4">
                  <FormElement className="">
                    <Label htmlFor={`variants.${variantId}.layout.variantOptionLayout`}>
                      Display as
                    </Label>
                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.variantOptionLayout.list`}
                          name="layout.variantOptionLayout"
                          value="list"
                        />
                        <Radio.Label
                          htmlFor={`variants.${variantId}.layout.variantOptionLayout.list`}
                        >
                          List
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.variantOptionLayout.grid`}
                          name="layout.variantOptionLayout"
                          value="grid"
                        />
                        <Radio.Label
                          htmlFor={`variants.${variantId}.layout.variantOptionLayout.grid`}
                        >
                          Grid
                        </Radio.Label>
                      </Radio.Wrapper>

                      {values.layout?.variantOptionLayout === 'grid' && (
                        <FormElement className="ml-5 mt-2 mb-2!">
                          <div className="w-32">
                            <Select
                              id={`variants.${variantId}.layout.variantOptionColumns`}
                              name="layout.variantOptionColumns"
                            >
                              <option value="1">1 column</option>
                              <option value="2">2 columns</option>
                              <option value="3">3 columns</option>
                              <option value="4">4 columns</option>
                              <option value="5">5 columns</option>
                              <option value="6">6 columns</option>
                              <option value="7">7 columns</option>
                              <option value="8">8 columns</option>
                              <option value="9">9 columns</option>
                              <option value="10">10 columns</option>
                            </Select>
                          </div>

                          <FormError name="layout.variantOptionColumns" />
                        </FormElement>
                      )}
                    </Radio.Group>
                    <FormError name="layout.type" />
                  </FormElement>

                  <FormElement>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.collapseVariantOptions`}
                        name="layout.collapseVariantOptions"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.collapseVariantOptions`}
                      >
                        Display as accordion{' '}
                        <InfoTooltip>
                          Useful when you have many options or multiple variants
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.collapseVariantOptions" />
                  </FormElement>

                  <FormElement className="space-y-2">
                    <Label htmlFor="textAlign">Alignment</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.left`}
                          name="layout.textAlign"
                          value="left"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.left`}>
                          Default
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.center`}
                          name="layout.textAlign"
                          value="center"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.center`}>
                          Center
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.stack`}
                          name="layout.textAlign"
                          value="stack"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.stack`}>
                          Stack
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                  </FormElement>

                  <FormElement className="space-y-2">
                    <Label htmlFor="imageSize">Image size</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.small`}
                          name="layout.imageSize"
                          value="small"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.small`}>
                          Small
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.medium`}
                          name="layout.imageSize"
                          value="medium"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.medium`}>
                          Medium
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.large`}
                          name="layout.imageSize"
                          value="large"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.large`}>
                          Large
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.layout.spacing`}>Spacing</Label>
                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.spacing.comfortable`}
                          name="layout.spacing"
                          value="comfortable"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.spacing.comfortable`}>
                          Comfortable
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.spacing.compact`}
                          name="layout.spacing"
                          value="compact"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.spacing.compact`}>
                          Compact
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                    <FormError name="layout.spacing" />
                  </FormElement>

                  <FormElement className="space-y-2">
                    <Label>Show on variant</Label>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantName`}
                        name="layout.showVariantName"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.layout.showVariantName`}>
                        Variant name
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantName" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionNames`}
                        name="layout.showVariantOptionNames"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionNames`}
                      >
                        Option names
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionNames" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionPrices`}
                        name="layout.showVariantOptionPrices"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionPrices`}
                      >
                        Option prices
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionPrices" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showPwywPlaceholder`}
                        name="layout.showPwywPlaceholder"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.layout.showPwywPlaceholder`}>
                        PWYW placeholder
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showPwywPlaceholder" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionPriceSign`}
                        name="layout.showVariantOptionPriceSign"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionPriceSign`}
                      >
                        Plus sign before price
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionPriceSign" />
                  </FormElement>
                </Collapsible.Content>
              </Collapsible.Root>

              <FormErrorBase>{status && status}</FormErrorBase>

              <FormSubmit align="end">
                <FormSubmit.State visible={!!dirty || !variant._id} />
                <FormSubmit.Cancel
                  onClick={() => {
                    if (variant.isNew) {
                      onCancelAddVariant();
                    } else {
                      setIsEditingVariant(false, variant._id || variant.key);
                    }
                  }}
                />
                <FormSubmit.Button>Save</FormSubmit.Button>
              </FormSubmit>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}
